/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import cx from 'classnames';
import { useTheme, Popover, useMergedRefs, SvgCaretRightSmall, } from '../utils/index.js';
import { Menu } from './Menu.js';
/**
 * Context used to provide menu item ref to sub-menu items.
 */
const MenuItemContext = React.createContext({ ref: undefined });
/**
 * Basic menu item component. Should be used inside `Menu` component for each item.
 */
export const MenuItem = React.forwardRef((props, ref) => {
    const { children, isSelected, disabled, value, onClick, sublabel, size = !!sublabel ? 'large' : 'default', icon, badge, className, style, role = 'menuitem', subMenuItems = [], ...rest } = props;
    useTheme();
    const menuItemRef = React.useRef(null);
    const refs = useMergedRefs(menuItemRef, ref);
    const { ref: parentMenuItemRef } = React.useContext(MenuItemContext);
    const subMenuRef = React.useRef(null);
    const [isSubmenuVisible, setIsSubmenuVisible] = React.useState(false);
    const onKeyDown = (event) => {
        var _a;
        if (event.altKey) {
            return;
        }
        switch (event.key) {
            case 'Enter':
            case ' ':
            case 'Spacebar': {
                !disabled && (onClick === null || onClick === void 0 ? void 0 : onClick(value));
                event.preventDefault();
                break;
            }
            case 'ArrowRight': {
                if (subMenuItems.length > 0) {
                    setIsSubmenuVisible(true);
                    event.preventDefault();
                }
                break;
            }
            case 'ArrowLeft': {
                (_a = parentMenuItemRef === null || parentMenuItemRef === void 0 ? void 0 : parentMenuItemRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                event.preventDefault();
                break;
            }
            default:
                break;
        }
    };
    const listItem = (React.createElement("li", { className: cx('iui-menu-item', {
            'iui-large': size === 'large',
            'iui-active': isSelected,
            'iui-disabled': disabled,
        }, className), onClick: () => !disabled && (onClick === null || onClick === void 0 ? void 0 : onClick(value)), ref: refs, style: style, role: role, tabIndex: disabled || role === 'presentation' ? undefined : -1, "aria-selected": isSelected, "aria-haspopup": subMenuItems.length > 0, "aria-disabled": disabled, onKeyDown: onKeyDown, onMouseEnter: () => setIsSubmenuVisible(true), onMouseLeave: (e) => {
            var _a;
            try {
                if (!((_a = subMenuRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))) {
                    setIsSubmenuVisible(false);
                }
            }
            catch (_b) { }
        }, ...rest },
        icon &&
            React.cloneElement(icon, {
                className: cx(icon.props.className, 'iui-icon'),
            }),
        React.createElement("span", { className: 'iui-content' },
            React.createElement("div", { className: 'iui-menu-label' }, children),
            sublabel && React.createElement("div", { className: 'iui-menu-description' }, sublabel)),
        !badge && subMenuItems.length > 0 && (React.createElement(SvgCaretRightSmall, { className: 'iui-icon' })),
        badge &&
            React.cloneElement(badge, {
                className: cx(badge.props.className, 'iui-icon'),
            })));
    return subMenuItems.length === 0 ? (listItem) : (React.createElement(MenuItemContext.Provider, { value: { ref: menuItemRef } },
        React.createElement(Popover, { placement: 'right-start', visible: isSubmenuVisible, appendTo: (item) => {
                var _a;
                // we want the div wrapping the parent menu because the menu has overflow,
                // which can cause the submenu to be cut off
                return (_a = item.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement;
            }, delay: 100, content: React.createElement("div", { onMouseLeave: () => setIsSubmenuVisible(false), onKeyDown: (e) => {
                    if (!e.altKey && e.key === 'ArrowLeft') {
                        setIsSubmenuVisible(false);
                        e.stopPropagation();
                    }
                } },
                React.createElement(Menu, { ref: subMenuRef, className: 'iui-scroll' }, subMenuItems)) }, listItem)));
});
export default MenuItem;
